import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { currentDateFnsLocale } from "../../../i18n/dateLocales";
import { PencilSquareIcon, TrashIcon } from "@heroicons/react/24/outline";
import { PMDialog } from "../../utils/PMDialog";
import { planGhostBtnClass } from "./projectPlanTheme";

function formatTime(iso) {
  if (!iso) return "—";
  try {
    return format(new Date(iso), "HH:mm");
  } catch {
    return "—";
  }
}

/**
 * Simple dialog for a single resource assignment on a given day: edit or delete that day only.
 */
export function PlanResourceDayDialog({
  open,
  onClose,
  event = null,
  resource = null,
  dayKey = null,
  employeeLabel = "",
  onEdit,
  onDelete,
}) {
  const { t } = useTranslation();

  const dateLabel = (() => {
    if (!dayKey) return "";
    try {
      return format(new Date(`${dayKey}T12:00:00`), "EEEE d. MMMM yyyy", {
        locale: currentDateFnsLocale(),
      });
    } catch {
      return dayKey;
    }
  })();

  const title = event?.title || t("project.plan.resource_day.untitled");
  const timeLabel = `${formatTime(resource?.start_time)} – ${formatTime(resource?.end_time)}`;

  const handleDelete = () => {
    const ok = window.confirm(
      t("project.plan.resource_day.delete_confirm", {
        name: employeeLabel || t("project.plan.resource_day.employee"),
        date: dateLabel || dayKey,
      })
    );
    if (!ok) return;
    onDelete?.(event, resource, dayKey);
    onClose?.();
  };

  return (
    <PMDialog
      open={open}
      onClose={onClose}
      title={t("project.plan.resource_day.title")}
      size="sm"
    >
      <div className="mb-4 rounded-[12px] border border-[#E5E9F0] bg-[#F8FAFC] px-3.5 py-3">
        <div className="text-[15px] font-semibold text-[#0B1220]">{title}</div>
        <div className="mt-1 text-sm text-[#6B7682]">
          {employeeLabel || t("project.plan.resource_day.employee")}
        </div>
        {dateLabel ? (
          <div className="mt-1 text-sm text-[#364152]">{dateLabel}</div>
        ) : null}
        <div className="mt-0.5 text-sm tabular-nums text-[#364152]">{timeLabel}</div>
      </div>

      <div className="flex flex-col gap-1.5">
        <button
          type="button"
          className="flex w-full items-center gap-3 rounded-[10px] border border-[#E5E9F0] bg-white px-3.5 py-3 text-left text-[14px] font-medium text-[#0B1220] hover:bg-[#F6F8FB]"
          onClick={() => {
            onEdit?.(event, resource, dayKey);
            onClose?.();
          }}
        >
          <PencilSquareIcon className="h-5 w-5 text-[#2B6CFF]" />
          {t("project.plan.resource_day.edit")}
        </button>
        <button
          type="button"
          className="flex w-full items-center gap-3 rounded-[10px] border border-red-100 bg-white px-3.5 py-3 text-left text-[14px] font-medium text-red-700 hover:bg-red-50"
          onClick={handleDelete}
        >
          <TrashIcon className="h-5 w-5" />
          {t("project.plan.resource_day.delete")}
        </button>
      </div>

      <div className="mt-5 flex justify-end">
        <button type="button" onClick={onClose} className={planGhostBtnClass}>
          {t("Close")}
        </button>
      </div>
    </PMDialog>
  );
}
